import { uniq } from 'lodash';
import type { Subscription } from 'rxjs';
import {
  clearGraphSelection,
  componentGraphSelectionChanged,
} from '@ardoq/component-selection';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { isTruthy } from '@ardoq/common-helpers';
import type { ArdoqId } from '@ardoq/api-types';
import type { AqBlocks, ItemSet } from 'tabview/visualizations/aq-blocks/types';

/**
 * The distinct components behind a diagram selection, for the view pane header's
 * selection toolbar. Keyed on a node's `modelId` rather than its `isComponent`
 * flag, so a group standing for a component is included — the opposite rule to
 * `doubleClick`. A group's `modelId` can instead be a type id or a field value,
 * which `componentIds` rejects, and grouping can draw one component as
 * several nodes, which `uniq` collapses.
 */
export const toGraphSelection = (
  items: ItemSet,
  componentIds: ReadonlySet<ArdoqId>
): ArdoqId[] =>
  uniq(
    [...items.nodes()]
      .map(node => node.modelId)
      .filter(isTruthy)
      .filter(modelId => componentIds.has(modelId))
  );

/** The element state `clearStaleCanvasSelection` drives, so a test needs no real element. */
type CanvasSelectionTarget = Pick<AqBlocks, 'selected' | 'pendRender'>;

/**
 * Drops a canvas selection the store has emptied. Only a component selection can
 * go stale that way: a reference or a group standing for no component reports
 * nothing to the store, so clearing on that would undo the click that made it.
 *
 * A selection holding one goes entirely, references included.
 */
export const clearStaleCanvasSelection = (
  element: CanvasSelectionTarget,
  componentIds: ReadonlySet<ArdoqId>
): void => {
  if (toGraphSelection(element.selected, componentIds).length === 0) return;
  element.selected.clear();
  element.pendRender();
};

/**
 * Subscribes the canvas to the store emptying its graph selection. The view's own
 * click dispatches `componentGraphSelectionChanged` synchronously from inside the
 * aq-blocks selection setter, so a clear that did not check the payload would undo
 * every selection as it was made.
 */
export const subscribeToStoreSelectionCleared = (
  getElement: () => CanvasSelectionTarget | null,
  getComponentIds: () => ReadonlySet<ArdoqId>
): Subscription[] => {
  const clear = () => {
    const element = getElement();
    if (element) clearStaleCanvasSelection(element, getComponentIds());
  };

  return [
    subscribeToAction(clearGraphSelection, clear),
    subscribeToAction(componentGraphSelectionChanged, ({ graphSelection }) => {
      if (graphSelection.length === 0) clear();
    }),
  ];
};
